import React, { FC, FormEvent, MouseEvent } from "react";

import { toast } from "react-toastify";

import {
  useInput,
  useBoolean,
  useArray,
  useDivisionSectionDropdown,
  useProvidersCurrentEmployee,
} from "~/hooks";

import {
  EmployeeLayout,
  EmployeeLayoutMainContent,
  EmployeeLayoutRightSidebar,
} from "~/components/layouts";

import { AiBusinessSectionContextDetails } from "./components/AiBusinessSectionContextDetails";
import { AiBusinessSectionContextForm } from "./components/AiBusinessSectionContextForm";
import { useApisManagersBusinessSectionsAiBusinessSectionContextsCreate } from "./hooks/useApisManagersBusinessSectionsAiBusinessSectionContextsCreate";
import { useApisManagersBusinessSectionsAiBusinessSectionContextsIndex } from "./hooks/useApisManagersBusinessSectionsAiBusinessSectionContextsIndex";

import { useAiCustomizeSlideNavigation } from "~/features/ai-customize/hooks/useAiCustomizeSlideNavigation";
import { AiBusinessSectionContextType } from "~/features/ai-customize/types";

export const ManagersAiCustomizeBusinessSectionContextsIndex: FC = () => {
  const { currentEmployee } = useProvidersCurrentEmployee();

  const {
    items: aiBusinessSectionContexts,
    setItems: setAiBusinessSectionContexts,
  } = useArray<AiBusinessSectionContextType>();

  const {
    selectableDivisions,
    selectedDivision,
    onDivisionChange,
    optionSelectableSections,
    optionSelectedSection,
    onSectionChange,
  } = useDivisionSectionDropdown({
    selectableDivisions: currentEmployee?.businessDivisions,
    selectableSections: currentEmployee?.businessSections,
  });

  const { fetchNextPage, refetch, hasNextPage, isFetching } =
    useApisManagersBusinessSectionsAiBusinessSectionContextsIndex({
      businessSectionId: optionSelectedSection?.id || "",
      config: {
        enabled: !!optionSelectedSection?.id,
        onSuccess: (data) => {
          setAiBusinessSectionContexts(
            data.pages.map((page) => page.aiBusinessSectionContexts).flat(),
          );
          const latestContext =
            data.pages[0]?.aiBusinessSectionContexts[0]?.context || "";
          setContext(latestContext);
        },
      },
    });

  const { navigations } = useAiCustomizeSlideNavigation({
    selectedArray: [false, true, false],
  });

  const breadCrumbItems = [
    {
      label: "AIカスタマイズ",
      href: "/managers/ai_costomize/business_section_contexts",
    },
  ];

  const {
    isChecked: isReadOnly,
    setTrue: setReadonly,
    setFalse: setNotReadonly,
  } = useBoolean(true);
  const [{ value: context, onChange: onChangeContext }, setContext] =
    useInput("");

  const { mutate: postRequest, isLoading: isSubmitting } =
    useApisManagersBusinessSectionsAiBusinessSectionContextsCreate();

  const handleReadonly = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setReadonly();
  };

  const handleNotReadonly = (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();

    setNotReadonly();
  };

  const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!optionSelectedSection?.id) {
      toast.error("部署・課を選択してください");
      return;
    }

    const body = {
      context,
    };

    postRequest(
      { businessSectionId: optionSelectedSection.id, body },
      {
        onSuccess: async (data) => {
          toast(data.message);
          setContext("");
          setReadonly();
          await refetch();
        },
      },
    );
  };

  return (
    <EmployeeLayout withRightSidebar breadCrumbItems={breadCrumbItems}>
      <EmployeeLayoutMainContent
        navigations={navigations}
        withRightSidebar
        className="space-y-6"
      >
        <AiBusinessSectionContextForm
          divisionSection={{
            selectableDivisions,
            selectedDivision,
            onDivisionChange,
            optionSelectableSections,
            optionSelectedSection,
            onSectionChange,
          }}
          context={{ value: context, onChange: onChangeContext }}
          isSubmitting={isSubmitting}
          handleSubmit={handleSubmit}
          handleReadonly={handleReadonly}
          handleNotReadonly={handleNotReadonly}
          isReadOnly={isReadOnly}
        />
        <AiBusinessSectionContextDetails
          aiBusinessSectionContexts={aiBusinessSectionContexts}
          isLoading={isFetching}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          refetch={refetch}
        />
      </EmployeeLayoutMainContent>
      <EmployeeLayoutRightSidebar />
    </EmployeeLayout>
  );
};
